// src/pages/type-produit/CreateTypeProduit.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const ProductTypeCreate = () => {
  const navigate = useNavigate();
  
  // États du formulaire
  const [typeProduit, setTypeProduit] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [errors, setErrors] = useState([]);

  // États pour l'erreur d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");

  // Fonction pour réinitialiser l'erreur d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };

  // Validation côté client
  const validateForm = () => {
    const newErrors = [];

    if (!typeProduit.trim()) {
      newErrors.push("Le type de produit est obligatoire.");
    }

    if (typeProduit.length > 250) {
      newErrors.push("Le type de produit ne peut pas dépasser 250 caractères.");
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Réinitialiser les messages
    setServerError("");
    setInfoMsg("");
    setErrors([]);
    resetAbonnementError();

    // Validation
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Token manquant. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/stock/type_produit/create_type_produit.php`,
        {
          type_produit: typeProduit.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Réponse création type produit:", response.data);

      if (response.data?.success) {
        setInfoMsg(response.data.message || "Type de produit créé avec succès.");
        
        // Réinitialiser le formulaire après succès
        setTypeProduit("");
        
        // Rediriger après un délai ou afficher un message de succès
        setTimeout(() => {
          navigate("/product-types");
        }, 1000);
      } else {
        // Gérer les erreurs spécifiques du serveur
        if (response.data?.errors) {
          setErrors(response.data.errors);
        }
        setServerError(response.data?.message || "Erreur lors de la création.");
      }
    } catch (err) {
      console.error("Erreur création type produit:", err);
      
      // 🔍 Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setLoading(false);
        return;
      }
      
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Permission refusée. Vous n'avez pas les droits nécessaires.");
      } else if (err.response?.status === 409) {
        setServerError("Ce type de produit existe déjà.");
      } else if (err.response?.status === 400) {
        if (err.response.data?.errors) {
          setErrors(err.response.data.errors);
        }
        setServerError(err.response.data?.message || "Erreurs de validation");
      } else if (err.response?.data?.message) {
        setServerError(err.response.data.message);
      } else {
        setServerError("Erreur réseau. Vérifiez votre connexion.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Voulez-vous annuler la création ? Les données saisies seront perdues.")) {
      navigate("/product-types");
    }
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/product-types" style={{ color: COLORS.primary }}>Types de produits</a>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              Nouveau type de produit
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ 
                      color: COLORS.primary,
                      paddingBottom: '16px',
                      width: '100%'
                    }}>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.success
                        }}>
                         <i className="bi bi-tag"></i>
                        </div>
                        Nouveau type de produit
                      </div>
                    </h5>
                  </div>
             </div>
          </div>

            {/* Affichage de l'erreur d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}

            {/* Messages d'erreur/succès */}
            {serverError && !abonnementError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg}
                    {infoMsg.includes("succès") && (
                      <div className="mt-2 small" style={{ color: '#1b5e20' }}>
                        <i className="bi bi-arrow-right me-1"></i>
                        Redirection vers la liste dans quelques secondes...
                      </div>
                    )}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {/* Affichage des erreurs de validation */}
            {errors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#f57c00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    <h6 className="alert-heading mb-2" style={{ color: '#e65100' }}>
                      Erreurs de validation
                    </h6>
                    <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>
                      {errors.map((error, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>{error}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setErrors([])}
                  ></button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Champ Type de produit */}
                <div className="col-md-12 mb-4">
                  <label htmlFor="typeProduit" className="form-label fw-bold  mb-3" style={{ 
                    fontSize: '0.95rem'
                  }}>
                   <span className="text-danger">*</span> Type de produit :
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.some(e => e.includes("type de produit")) ? "is-invalid" : ""}`}
                    id="typeProduit"
                    placeholder="Ex: Électronique, Vêtements, Alimentation..."
                    value={typeProduit}
                    onChange={(e) => setTypeProduit(e.target.value)}
                    disabled={loading}
                    maxLength={250}
                    style={{
                      borderColor: errors.some(e => e.includes("type de produit")) ? COLORS.danger : '#ced4da',
                      borderRadius: '6px',
                      padding: '10px 12px',
                      fontSize: '0.95rem',
                      backgroundColor: COLORS.white,
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = COLORS.secondary;
                      e.target.style.boxShadow = `0 0 0 0.2rem rgba(30, 136, 200, 0.1)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ced4da';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <div className="mt-3 d-flex justify-content-between align-items-center">
                    <div className="form-text" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <i className="bi bi-info-circle me-1"></i>
                      Saisissez le nom du type de produit (max. 250 caractères)
                    </div>
                    {typeProduit.length > 0 && (
                      <span className={`badge fw-semibold ${typeProduit.length > 250 ? 'bg-danger' : 'bg-info'}`} style={{ 
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '0.75rem'
                      }}>
                        {typeProduit.length}/250
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="mt-5 pt-4 border-top" style={{ borderColor: '#e0e0e0' }}>
                <div className="d-flex justify-content-end gap-3">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={loading}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '120px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="submit"
                    className="btn fw-semibold"
                    disabled={loading}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '180px'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Création...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2 fw-bold"></i>
                        Créer le type
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .form-control.is-invalid {
          border-color: ${COLORS.danger};
          background-color: #fff5f5;
        }
        
        .form-control.is-invalid:focus {
          box-shadow: 0 0 0 0.2rem rgba(244, 67, 54, 0.25);
        }
        
        .breadcrumb-item a {
          text-decoration: none;
          transition: color 0.3s ease;
        }
        
        .breadcrumb-item a:hover {
          color: ${COLORS.secondary} !important;
        }
        
        .badge.bg-info {
          background-color: ${COLORS.info} !important;
        }
        
        .badge.bg-danger {
          background-color: ${COLORS.danger} !important;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-end {
            flex-direction: column;
            gap: 12px;
          }
          
          .btn {
            width: 100%;
            justify-content: center;
          }
          
          .col-md-6 {
            margin-bottom: 16px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ProductTypeCreate;